import { BulkRequestQueryKey } from "api/clients/useBulkRequestQuery";
import { type QueryClient, type QueryState, useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesFor } from "@vyne-shared/utils/invalidateQueriesFor";
import { useEffect } from "react";
import type { PaymentReportResult } from "api/responses";

/**
 * Invalidate shared related queries for the pay now and refund flow and keep refetching for a certain time.
 * @param invalidationType the type of invalidations to perform.
 * @param patientId the patient ID to invalidate queries for.
 * @param shouldInvalidate whether the invalidations should run.
 * @param [paymentReportUntilState] check against the payment report state to determine when data loads from backend.
 */
export function useSharedTransactionInvalidation(
    invalidationType: "payment" | "refund",
    patientId: number,
    transactionId: number | null,
    shouldInvalidate: boolean,
    paymentReportUntilState?: (state: QueryState<PaymentReportResult>) => boolean,
) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (shouldInvalidate) {
            invalidateAfterTransaction(
                queryClient,
                invalidationType,
                patientId,
                transactionId,
                paymentReportUntilState,
            );
        }
    }, [patientId, invalidationType, paymentReportUntilState, queryClient, shouldInvalidate, transactionId]);
}

export function invalidateAfterTransaction(
    queryClient: QueryClient,
    invalidationType: "payment" | "refund",
    patientId: number,
    transactionId: number | null,
    paymentReportUntilState?: (state: QueryState<PaymentReportResult>) => boolean,
) {
    void invalidateQueriesFor(queryClient, 15000, ["transactionHistory", patientId]);
    void invalidateQueriesFor(queryClient, 15000, ["paymentReport"], paymentReportUntilState);

    // refund specific invalidations
    if (invalidationType === "refund") {
        void invalidateQueriesFor(queryClient, 15000, ["singleTransactionHistory", patientId, transactionId]);
    }

    // payment specific invalidations (transaction and balance change)
    if (invalidationType === "payment") {
        void invalidateQueriesFor(queryClient, 20000, ["patientActivity"]);
        void invalidateQueriesFor(queryClient, 20000, ["summary", patientId]);
        void invalidateQueriesFor(queryClient, 10000, BulkRequestQueryKey);
        void queryClient.invalidateQueries({ queryKey: ["metrics"] });
        void queryClient.invalidateQueries({ queryKey: ["searchPatientField"] });
    }
}
